import {
  exportProductsMinimumStockToXlsx,
  useProducts,
  useReapprosExport,
  useReapprosImport,
  useStoreStock,
} from '@gozoki/api'
import type { DailyMinsAndSold } from '@gozoki/api-types'
import { includesLowerCase } from '@gozoki/tools'
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import axios from 'axios'
import { Laptop } from 'lucide-react'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useRef, useState } from 'react'
import { api } from '~/lib/tuyau'
import { AuthorizedButtonLink, withRoutePermissions } from '~/utils/rbac'
import { DisabledButton } from '../../../components/buttons/DisabledButton'
import { useToast } from '../../../components/communication/Toast'
import ImportExportIcon from '../../../components/icons/ImportExportIcon'
import Page from '../../../components/Page'
import { DailySalesGrid } from '../../../components/routes/daily-sales/DailySalesGrid'
import sumArrays from '../../../utils/arrays/sumArrays'
import { FourDatePicker, type DateStateProps } from '../../../utils/dashboard/fourDatePicker'
import { InternalCategoryPicker } from '../../../utils/dashboard/internalCategoryPicker'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { timeRanges } from '../../../utils/dashboard/timeRanges'
import { downloadBlob } from '../../../utils/excel/downloadBlob'
import { extractXlsxErrors } from '../../../utils/excel/extractXlsxErrors'
import { useRights } from '../../../utils/hooks'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/daily-sales/')({
  staticData: {
    permission: ['read', 'daily-sales'],
  },
  component: DailySalesPage,
})
withRoutePermissions(Route)

const defaultStockMinCompute = {
  breakageRate: 2.1,
  growthRate: 1.1,
  lookbackWeeks: 2,
} as const

function DailySalesPage() {
  const isUserAdmin = useRights(['ADMIN'])
  const isSuperAdmin = useRights(['SUPER_ADMIN'])
  const [Toast, showToast] = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [currentStore, setCurrentStore] = useState<number>(3)
  const [currentInternalCategory, setCurrentInternalCategory] = useState<number>(0)
  const { data: products } = useProducts()
  const { data: allKpis } = useQuery(
    api.stores.getDashboardKpis.queryOptions({}, { staleTime: 60_000 }),
  )
  const { data: stock, refetch: refetchStock } = useStoreStock(currentStore)
  const [search, setSearch] = useState('')

  const { mutate: exportReappros } = useReapprosExport()
  const { mutate: importReappros } = useReapprosImport()

  const today = new Date()
  const oneMonthBefore = new Date()
  oneMonthBefore.setMonth(today.getMonth() - 1)

  const [currentTimeRange, setCurrentTimeRange] = useState<number>(-1)
  const [currentDateStart, setCurrentDateStart] = useState<Date>(oneMonthBefore)
  const [currentDateEnd, setCurrentDateEnd] = useState<Date>(today)
  const [openComputeColapse, setOpenComputaColapse] = useState<boolean>(false)
  const [autoCalculate, setAutoCalculate] = useState<boolean>(false)

  // Auto calculate
  const [breakageRate, setBreakageRate] = useState<number>(defaultStockMinCompute.breakageRate)
  const [growthRate, setGrowthRate] = useState<number>(defaultStockMinCompute.growthRate)
  const [recoil, setRecoil] = useState<1 | 2 | 3 | 4>(defaultStockMinCompute.lookbackWeeks)
  const isInternalCategorySelected = currentInternalCategory !== 0

  const { data: storeCategoriesMinimumStockComputes } = useQuery(
    api.stores.getStoreCategoriesMinimumStockComputes.queryOptions({
      params: { id: currentStore },
    }),
  )

  const allProducts = useMemo(
    () => (products ? products.filter((p) => p.isActive) : []),
    [products],
  )

  const [page, setPage] = useState(0)

  const rowsPerPage = 1000

  const currentStoreKpis = useMemo(() => {
    if (!allKpis) {
      return []
    }
    return allKpis?.filter(
      (kpi) =>
        currentStore === kpi.storeId &&
        new Date(kpi.date).getTime() >= currentDateStart.getTime() &&
        new Date(kpi.date).getTime() <= currentDateEnd.getTime(),
    )
  }, [allKpis, currentStore, currentDateStart, currentDateEnd])

  const storeKpis = useMemo(() => {
    if (!allKpis) {
      return []
    }
    return allKpis?.filter((kpi) => currentStore === kpi.storeId)
  }, [allKpis, currentStore])

  const dailyMinsAndSold = useMemo(() => {
    const daily: DailyMinsAndSold = {}
    stock?.rows.forEach((row) => {
      daily[row.reference] = {
        minMap: row.dailyMinStocksMap,
        soldMap: {},
      }
    })

    currentStoreKpis.forEach((kpi) => {
      const jsDay = new Date(kpi.date).getDay() // 0 = dimanche, 6 = samedi !!
      const day = jsDay === 0 ? 6 : jsDay - 1

      allProducts.forEach(({ reference }) => {
        if (kpi.productsSoldMap[reference]) {
          if (!daily[reference]) {
            daily[reference] = { minMap: {}, soldMap: {} }
          }
          if (!daily[reference].soldMap[day]) {
            daily[reference].soldMap[day] = {
              history: [],
              totalCount: 0,
              totalDays: 0,
            }
          }
          daily[reference].soldMap[day].totalCount += kpi.productsSoldMap[reference].count
          daily[reference].soldMap[day].totalDays += 1
        }
      })
    })

    storeKpis
      ?.sort((a, b) => b.date.localeCompare(a.date))
      ?.forEach((kpi) => {
        const jsDay = new Date(kpi.date).getDay() // 0 = dimanche, 6 = samedi !!
        const day = jsDay === 0 ? 6 : jsDay - 1

        allProducts.forEach(({ reference }) => {
          if (daily[reference]?.soldMap[day]) {
            daily[reference].soldMap[day].history.push(kpi.productsSoldMap[reference]?.count ?? 0)
          }
        })
      })

    return daily
  }, [stock?.rows, currentStoreKpis, storeKpis, allProducts])

  const currentCategoryProducts = useMemo(() => {
    if (!allProducts) {
      return []
    }
    if (currentInternalCategory === 0) {
      return allProducts
    }
    return allProducts.filter((product) => product.internalCategory.id === currentInternalCategory)
  }, [currentInternalCategory, allProducts])

  const currentSearchProducts = useMemo(() => {
    if (!currentCategoryProducts) {
      return []
    }
    return currentCategoryProducts.filter((product) => includesLowerCase(product.label, search))
  }, [currentCategoryProducts, search])

  const productsPaginated = useMemo(
    () => currentSearchProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [currentSearchProducts, page, rowsPerPage],
  )

  const { mutate: downloadProductMinimumStock } = useMutation({
    mutationFn: exportProductsMinimumStockToXlsx,
    onError: async (error: { message: string; response: { data: Blob } }) => {
      console.error(error)
      if (error?.response?.data?.size) {
        // this is a blob
        const text = await error.response.data.text()
        const json: { message: string } = JSON.parse(text)
        showToast({
          severity: 'error',
          message: json.message as string,
        })
      } else {
        showToast({
          severity: 'error',
          message: error.message as string,
        })
      }
    },
    onSuccess: (response) => {
      const url = window.URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = url
      link.download = `product_minimum_stock.xlsx`
      link.click()
      URL.revokeObjectURL(url)
    },
  })

  const handleImportReappros = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    importReappros(file, {
      onError: (error) => {
        const errorMessage = axios.isAxiosError(error)
          ? (error.response?.data?.message ??
            "Une erreur inconnue est survenue lors de l'importation des reappros")
          : error.message
        showToast({
          severity: 'error',
          message: errorMessage,
        })
      },
      onSettled: () => {
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      },
      onSuccess: () => {
        showToast({
          severity: 'success',
          message: 'Réappros importés avec succès',
        })
      },
    })
  }

  const handleExportReappros = () => {
    exportReappros(undefined, {
      onError: async (error) => {
        const rowErrors = await extractXlsxErrors(error)
        const firstError = rowErrors.errors?.at(0)
        showToast({
          severity: 'error',
          message: firstError
            ? `Erreur sur le ligne ${firstError?.row}: "${firstError?.field}" - ${firstError?.message}`
            : rowErrors.message,
        })
      },
      onSuccess: (data) => {
        downloadBlob({
          blob: data,
          filename: `reappros_${DateTime.now().toFormat('yyyy-MM-dd')}.xlsx`,
        })
      },
    })
  }

  const dateState: DateStateProps = useMemo(
    () => ({
      timeRanges: {
        all: timeRanges.filter((t) => t.value > 1),
        current: currentTimeRange,
        set: setCurrentTimeRange,
      },
      currentDate: {
        start: currentDateStart,
        setStart: setCurrentDateStart,
        end: currentDateEnd,
        setEnd: setCurrentDateEnd,
      },
    }),
    [currentTimeRange, currentDateStart, currentDateEnd],
  )

  const handleChangeInternalCategory = useCallback(
    (value: number) => {
      setPage(0)
      setCurrentInternalCategory(value)

      // Update the auto compute stock minimum values
      const newMinStockCompute =
        storeCategoriesMinimumStockComputes?.find((c) => c.internalCategoryId === value) ??
        defaultStockMinCompute

      setBreakageRate(newMinStockCompute?.breakageRate ?? defaultStockMinCompute.breakageRate)
      setGrowthRate(newMinStockCompute?.growthRate ?? defaultStockMinCompute.growthRate)
      setRecoil(newMinStockCompute?.lookbackWeeks ?? defaultStockMinCompute.lookbackWeeks)
    },
    [setPage, setCurrentInternalCategory, storeCategoriesMinimumStockComputes],
  )

  const calculateMinimumStock = useCallback(
    (reference: string) => {
      let totalSum = 0
      let totalCount = 0
      let weekSum = 0
      let weekCount = 0

      return [0, 1, 2, 3, 4, 5].map((day) => {
        let rawRecoils = []
        if (day === 5) {
          const saturdayRecoil =
            dailyMinsAndSold[reference].soldMap[day]?.history?.slice(0, recoil) ?? []
          const sundayRecoil =
            dailyMinsAndSold[reference].soldMap[6]?.history?.slice(0, recoil) ?? []
          rawRecoils = sumArrays(saturdayRecoil, sundayRecoil)
        } else {
          rawRecoils = dailyMinsAndSold[reference].soldMap[day]?.history?.slice(0, recoil) ?? []
        }

        const othercategoryRecoils = productsPaginated.map((product) => {
          if (day === 5) {
            // Whee need to include saturday and sunday in the recoil
            const saturdayRecoil =
              dailyMinsAndSold[product.reference].soldMap[5]?.history?.slice(0, recoil) ?? []
            const sundayRecoil =
              dailyMinsAndSold[product.reference].soldMap[6]?.history?.slice(0, recoil) ?? []
            return sumArrays(saturdayRecoil, sundayRecoil)
          }
          return dailyMinsAndSold[product.reference].soldMap[day]?.history?.slice(0, recoil) ?? []
        })
        const computedRecoil = Array.from({ length: recoil }, (_, i) => {
          let otherCategorySum = 0
          let otherCategoryCount = 0
          othercategoryRecoils.forEach((recoils) => {
            if (recoils[i]) {
              otherCategorySum += recoils[i]
              otherCategoryCount += day === 5 ? 2 : 1
              totalSum += recoils[i]
              totalCount += day === 5 ? 2 : 1
              if (i === 0) {
                weekSum += recoils[i]
                weekCount += day === 5 ? 2 : 1
              }
            }
          })
          const averageOtherCategory = otherCategorySum / otherCategoryCount
          if (rawRecoils[i]) {
            return rawRecoils[i]
          }
          if (averageOtherCategory) {
            return averageOtherCategory
          }
          return 0
        })

        const averageRecoil = computedRecoil.reduce((a, b) => a + b, 0) / recoil
        let auroGrowthRate = 1
        if (recoil > 1) {
          // If recoil = 1 we don't want to apply the growth rate
          const averageWeek = weekSum / weekCount
          const totalAverage = totalSum / totalCount
          auroGrowthRate = averageWeek / totalAverage
        }
        const minimumStock = averageRecoil * breakageRate * growthRate * auroGrowthRate
        return Math.floor(minimumStock + 1)
      })
    },
    [dailyMinsAndSold, recoil, productsPaginated, breakageRate, growthRate],
  )

  const {
    mutate: saveAutoComputeStockMinimumMutation,
    isPending: isSavingAutoComputeStockMinimum,
  } = useMutation(
    api.stores.updateCategoryMinimumStockCompute.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries(
          api.stores.getStoreCategoriesMinimumStockComputes.queryFilter({
            params: { id: variables.params.id },
          }),
        )
      },
    }),
  )

  const handleSaveAutoComputeStockMinimum = () => {
    saveAutoComputeStockMinimumMutation(
      {
        params: { id: currentStore },
        body: {
          breakageRate,
          growthRate,
          internalCategoryId: currentInternalCategory,
          lookbackWeeks: recoil,
        },
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Valeurs de calcul du stock minimal sauvegardées',
            severity: 'success',
          })
        },
      },
    )
  }

  const handleEndAutoCalculate = useCallback(() => {
    setAutoCalculate(false)
  }, [])

  return (
    <Page sx={fonts.roboto}>
      <Toast />
      <div style={{ display: 'flex', paddingBottom: 20, paddingLeft: 20 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Ventes journalières
        </Typography>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 1 }}>
          <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'end' }}>
            <h3 style={fonts.roboto}>Besoins de réapprovisionnement:</h3>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                marginLeft: '16px',
                marginRight: '16px',
              }}
              onClick={() => handleExportReappros()}
              disabled={!isUserAdmin}
            >
              <ImportExportIcon />
              Exporter
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => fileInputRef.current?.click()}
              disabled={!isUserAdmin}
            >
              <ImportExportIcon />
              Importer
              <input type="file" onChange={handleImportReappros} hidden ref={fileInputRef} />
            </Button>
          </div>
          <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'end' }}>
            <h3 style={fonts.roboto}>Stocks minimum:</h3>
            <DisabledButton
              buttonProps={{
                color: 'warning',
                disabled: !isSuperAdmin,
                onClick: () => setOpenComputaColapse(!openComputeColapse),
                sx: {
                  marginLeft: '16px',
                },
                variant: 'contained',
              }}
              disableReason="Le calcul automatique n'est pas disponible que pour les super admins"
            >
              <Laptop />
              &nbsp;&nbsp;Calcul automatique
            </DisabledButton>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                marginLeft: '16px',
                marginRight: '16px',
              }}
              onClick={() => downloadProductMinimumStock()}
              disabled={!isUserAdmin}
            >
              <ImportExportIcon />
              Exporter
            </Button>
            <AuthorizedButtonLink
              variant="contained"
              color="secondary"
              to="/daily-sales/stock-min/import"
              disabled={!isUserAdmin}
            >
              <ImportExportIcon />
              Importer
            </AuthorizedButtonLink>
          </div>
        </div>
      </div>
      <Stack gap={1} p={1} flexDirection="row">
        <FourDatePicker dateState={dateState} />
        <StoresPicker onlyBoutiques setUniqueStore={setCurrentStore} defaultStore={currentStore} />
        <InternalCategoryPicker setCurrentCategory={handleChangeInternalCategory} />
      </Stack>
      {/* <Collapse in={openComputeColapse} orientation="horizontal" collapsedSize={0}> */}
      <Paper
        sx={{
          display: openComputeColapse ? 'block' : 'none',
          marginTop: '16px',
          padding: 2,
        }}
      >
        <Typography variant="h6" align="center" sx={{ marginBottom: 1 }}>
          Calcul automatique des stocks minimum
        </Typography>
        <Stack gap={1} p={1} flexDirection="row">
          <TextField
            variant="outlined"
            label="Taux de rupture"
            value={breakageRate}
            onChange={(e) => setBreakageRate(Number(e.target.value))}
            type="number"
            inputProps={{
              max: 5,
              min: 0,
              step: 0.1,
            }}
            sx={{
              flex: 1,
              minHeight: '60px',
            }}
          />
          <TextField
            variant="outlined"
            label="Taux de croissance"
            value={growthRate}
            onChange={(e) => setGrowthRate(Number(e.target.value))}
            type="number"
            inputProps={{
              max: 5,
              min: 0,
              step: 0.1,
            }}
            sx={{
              flex: 1,
              minHeight: '60px',
            }}
          />
          <FormControl
            sx={{
              flex: 1,
              minHeight: '60px',
            }}
          >
            <InputLabel>Recul en semaines</InputLabel>
            <Select
              variant="outlined"
              label="Recul en semaines"
              value={recoil}
              onChange={(e) => setRecoil(e.target.value as 1 | 2 | 3 | 4)}
              sx={{
                minHeight: '56px',
              }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
          <div style={{ flex: 1 }} />
          {isInternalCategorySelected && (
            <Button
              variant="contained"
              color="primary"
              sx={{ alignSelf: 'center', minHeight: '56px', minWidth: 150 }}
              onClick={handleSaveAutoComputeStockMinimum}
              disabled={isSavingAutoComputeStockMinimum}
            >
              Sauvegarder
              {isSavingAutoComputeStockMinimum && <CircularProgress size={20} />}
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            sx={{ alignSelf: 'center', minHeight: '56px', minWidth: 150 }}
            onClick={() => {
              setAutoCalculate(!autoCalculate)
            }}
          >
            Calculer
          </Button>
        </Stack>
      </Paper>
      {/* </Collapse> */}
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
            }}
          />
          <div style={{ flexGrow: 1 }} />
        </div>
        <DailySalesGrid
          storeId={currentStore}
          dailyMinsAndSold={dailyMinsAndSold}
          products={productsPaginated}
          refetch={refetchStock}
          autoCalculate={autoCalculate}
          endAutoCalculate={handleEndAutoCalculate}
          calculateMinimumStock={calculateMinimumStock}
        />
        <TablePagination
          component="div"
          count={currentSearchProducts.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
        />
      </Paper>
    </Page>
  )
}
